import { computed, inject, onBeforeUnmount, onMounted, ref } from 'vue'
import { useEcxToast } from '@edgeconnex/design-system'
import { getApiErrorMessage } from '@/utils/errorUtils'
import { stashImpersonationNotice } from '@/utils/impersonationNotice'
import type { ILoggedUserService } from '@/services/LoggedUserService'
import { UserPreferencesService } from '@/services/UserPreferencesService'

export function useImpersonationSession() {
  const loggedUserService = inject<ILoggedUserService>('loggedUserService')!
  const toast = useEcxToast()

  const stopping = ref(false)
  const now = ref(Date.now())
  let tickHandle: number | null = null

  onMounted(() => {
    tickHandle = window.setInterval(() => {
      now.value = Date.now()
    }, 1000)
  })

  onBeforeUnmount(() => {
    if (tickHandle !== null) {
      window.clearInterval(tickHandle)
      tickHandle = null
    }
  })

  const remaining = computed(() => {
    const expiresTS = loggedUserService.impersonation.value?.expiresTS
    if (!expiresTS) return null
    const ms = new Date(expiresTS).getTime() - now.value
    if (ms <= 0) return '00:00:00'
    const totalSeconds = Math.floor(ms / 1000)
    const hh = Math.floor(totalSeconds / 3600).toString().padStart(2, '0')
    const mm = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0')
    const ss = (totalSeconds % 60).toString().padStart(2, '0')
    return `${hh}:${mm}:${ss}`
  })

  async function stopImpersonation() {
    if (stopping.value) return
    stopping.value = true
    try {
      await loggedUserService.stopImpersonationAsync()
      UserPreferencesService.reset()
      // Hard reload from home so identity + CASL abilities fully reset; the toast is stashed to
      // survive the reload and shown on next mount.
      stashImpersonationNotice({ severity: 'error', summary: 'Impersonation ended', life: 3000 })
      window.location.href = '/'
    } catch (err) {
      console.error('stopImpersonation failed', err)
      toast.add({
        severity: 'error',
        summary: 'Unable to stop impersonation',
        detail: getApiErrorMessage(err, 'Please try again.'),
        life: 5000,
      })
    } finally {
      stopping.value = false
    }
  }

  return { remaining, stopping, stopImpersonation }
}
